import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Edit, Filter, Download, TrendingUp, TrendingDown, FileText, Trash2, Copy } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useOpsDashboard } from '@/context/OpsDashboardContext';

interface DemandSummary {
  total_brands: number;
  committed_and_signed: number;
  total_pipeline_budget: number;
  committed_budget: number;
  avg_target_ecpm: number;
}

interface PreferenceSignal {
  show_name: string;
  direction: 'up' | 'down';
  created_at: string;
}

interface ScriptContextRequest {
  project_name: string;
  campaign_name: string;
  status: 'pending' | 'approved' | 'completed' | 'rejected';
  request_type: string;
  requested_at: string;
  user_email: string;
  message?: string;
  created_at?: string;
}

interface Brand {
  id: string;
  advertiser: string;
  agency?: string;
  email?: string;
  vertical: string;
  competitive_categories: string[];
  budget: number;
  target_ecpm: number;
  campaign_window: string;
  suitability_requirements: string[];
  preferred_verticals: string[];
  status: 'lead' | 'in_conversation' | 'committed' | 'signed';
  notes?: string;
  preference_signals?: PreferenceSignal[];
  script_context_requests?: ScriptContextRequest[];
  created_at: string;
  updated_at: string;
}

interface DemandViewResponse {
  summary: DemandSummary;
  brands: Brand[];
  total: number;
  page: number;
  limit: number;
}

interface BrandFormData {
  advertiser: string;
  agency: string;
  email: string;
  vertical: string;
  competitive_categories: string[];
  budget: number;
  target_ecpm: number;
  campaign_window: string;
  suitability_requirements: string[];
  preferred_verticals: string[];
  status: string;
  notes: string;
}

type BrandFormErrors = Partial<Record<keyof BrandFormData, string>>;
const PAGE_SIZE = 15;

const DemandViewPage: React.FC = () => {
  const [data, setData] = useState<DemandViewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const fetchSeqRef = useRef(0);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [brandToDelete, setBrandToDelete] = useState<Brand | null>(null);
  const [selectedBrandSignals, setSelectedBrandSignals] = useState<{brand: Brand, signals: PreferenceSignal[]} | null>(null);
  const [selectedBrandScriptRequests, setSelectedBrandScriptRequests] = useState<{brand: Brand, requests: ScriptContextRequest[]} | null>(null);
  const [showFilters, setShowFilters] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    vertical: '',
    budget_min: '',
    budget_max: '',
    search: ''
  });
  const [page, setPage] = useState(1);
  const [formData, setFormData] = useState<BrandFormData>({
    advertiser: '',
    agency: '',
    email: '',
    vertical: '',
    competitive_categories: [],
    budget: 0,
    target_ecpm: 0,
    campaign_window: '',
    suitability_requirements: [],
    preferred_verticals: [],
    status: 'lead',
    notes: ''
  });
  const [formErrors, setFormErrors] = useState<BrandFormErrors>({});
  const { toast } = useToast();
  const { refreshTriggers } = useOpsDashboard();

  const verticals = [
    'Tech', 'Lifestyle', 'Food & Bev', 'Auto', 'Finance',
    'Sports', 'Drama', 'Comedy', 'Documentary', 'Reality'
  ];

  const competitiveCategories = [
    'CPG/Personal Care', 'Automotive', 'Financial Services',
    'Technology', 'Entertainment', 'Food & Beverage', 'Healthcare',
    'Retail', 'Travel', 'Gaming'
  ];

  const suitabilityOptions = [
    'Violence', 'Adult Content', 'Political Content', 'Controversial Topics',
    'Competitor Mentions', 'Negative News', 'User-Generated Content'
  ];

  const fetchDemandData = async () => {
    const isInitialLoad = !data;
    const normalizedSearch = filters.search.trim();
    const fetchSeq = ++fetchSeqRef.current;
    try {
      if (isInitialLoad) setLoading(true);
      else setIsFetching(true);

      // Cancel any in-flight request so out-of-order responses can't overwrite newer search.
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      const params = new URLSearchParams({
        page: page.toString(),
        limit: PAGE_SIZE.toString(),
        ...(filters.status && filters.status !== 'all' && { status: filters.status }),
        ...(filters.vertical && filters.vertical !== 'all' && { vertical: filters.vertical }),
        ...(filters.budget_min && { budget_min: filters.budget_min }),
        ...(filters.budget_max && { budget_max: filters.budget_max }),
        ...(normalizedSearch && { search: normalizedSearch })
      });

      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/v1/ops-dashboard/demand?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error('Failed to fetch demand data');
      }

      const result = await response.json();
      if (fetchSeq === fetchSeqRef.current) {
        setData(result);
      }
    } catch (error) {
      if ((error as any)?.name === 'AbortError') return;
      console.error('Error fetching demand data:', error);
      toast({
        title: "Error",
        description: "Failed to fetch demand data",
        variant: "destructive"
      });
    } finally {
      if (fetchSeq === fetchSeqRef.current) {
        if (isInitialLoad) setLoading(false);
        else setIsFetching(false);
      }
    }
  };

  const validateBrandForm = (): BrandFormErrors => {
    const errors: BrandFormErrors = {};

    if (!formData.advertiser.trim()) {
      errors.advertiser = 'Advertiser is required.';
    }
    if (!formData.vertical.trim()) {
      errors.vertical = 'Vertical is required.';
    }
    if (!formData.campaign_window.trim()) {
      errors.campaign_window = 'Campaign window is required.';
    }
    // Competitive categories are optional - removed required validation
    // if (!formData.competitive_categories.length) {
    //   errors.competitive_categories = 'Select at least one category.';
    // }
    if (!Number.isFinite(formData.budget) || formData.budget <= 0) {
      errors.budget = 'Budget must be greater than 0.';
    }
    if (!Number.isFinite(formData.target_ecpm) || formData.target_ecpm <= 0) {
      errors.target_ecpm = 'Target eCPM must be greater than 0.';
    }
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Enter a valid email.';
    }

    return errors;
  };

  const handleCreateBrand = async () => {
    const errors = validateBrandForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast({
        title: "Validation Error",
        description: "Please correct the highlighted fields.",
        variant: "destructive"
      });
      return;
    }

    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/v1/ops-dashboard/demand`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to create brand');
      }

      toast({
        title: "Success",
        description: "Brand created successfully"
      });

      setIsCreateDialogOpen(false);
      resetForm();
      
      // Add a small delay to ensure the backend has processed the creation
      setTimeout(() => {
        fetchDemandData();
      }, 100);
    } catch (error) {
      console.error('Error creating brand:', error);
      toast({
        title: "Error",
        description: "Failed to create brand",
        variant: "destructive"
      });
    }
  };

  const handleUpdateBrand = async () => {
    if (!editingBrand) return;
    const errors = validateBrandForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast({
        title: "Validation Error",
        description: "Please correct the highlighted fields.",
        variant: "destructive"
      });
      return;
    }

    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/v1/ops-dashboard/demand/${editingBrand.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to update brand');
      }

      const updatedBrand: Brand = await response.json();

      // Optimistically sync the edited row so reopening Edit immediately
      // shows the latest saved values without waiting for a list refetch.
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          brands: prev.brands.map((brand) =>
            brand.id === updatedBrand.id ? updatedBrand : brand
          )
        };
      });

      toast({
        title: "Success",
        description: "Brand updated successfully"
      });

      setIsEditDialogOpen(false);
      setEditingBrand(null);
      resetForm();
      
      // Add a small delay to ensure the backend has processed the update
      setTimeout(() => {
        fetchDemandData();
      }, 100);
    } catch (error) {
      console.error('Error updating brand:', error);
      toast({
        title: "Error",
        description: "Failed to update brand",
        variant: "destructive"
      });
    }
  };

  const handleDeleteBrand = async () => {
    if (!brandToDelete) return;

    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/v1/ops-dashboard/demand/${brandToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to delete brand');
      }

      const result = await response.json();

      toast({
        title: "Success",
        description: result.message || "Brand deleted successfully"
      });

      setIsDeleteDialogOpen(false);
      setBrandToDelete(null);
      
      // Add a small delay to ensure the backend has processed the deletion
      setTimeout(() => {
        fetchDemandData();
      }, 100);
    } catch (error) {
      console.error('Error deleting brand:', error);
      toast({
        title: "Error",
        description: "Failed to delete brand",
        variant: "destructive"
      });
    }
  };

  const openDeleteDialog = (brand: Brand) => {
    setBrandToDelete(brand);
    setIsDeleteDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      advertiser: '',
      agency: '',
      email: '',
      vertical: '',
      competitive_categories: [],
      budget: 0,
      target_ecpm: 0,
      campaign_window: '',
      suitability_requirements: [],
      preferred_verticals: [],
      status: 'lead',
      notes: ''
    });
    setFormErrors({});
  };

  const openEditDialog = (brand: Brand) => {
    setEditingBrand(brand);
    setFormData({
      advertiser: brand.advertiser,
      agency: brand.agency || '',
      email: brand.email || '',
      vertical: brand.vertical,
      competitive_categories: brand.competitive_categories,
      budget: brand.budget,
      target_ecpm: brand.target_ecpm,
      campaign_window: brand.campaign_window,
      suitability_requirements: brand.suitability_requirements,
      preferred_verticals: brand.preferred_verticals,
      status: brand.status,
      notes: brand.notes || ''
    });
    setIsEditDialogOpen(true);
    setFormErrors({});
  };

  useEffect(() => {
    fetchDemandData();
  }, [page, filters]);

  useEffect(() => {
    setPage(1);
  }, [filters.search]);

  // Listen for refresh triggers from the context
  useEffect(() => {
    if (refreshTriggers.demand > 0) {
      fetchDemandData();
    }
  }, [refreshTriggers.demand]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'signed': return 'bg-green-100 text-green-800';
      case 'committed': return 'bg-blue-100 text-blue-800';
      case 'in_conversation': return 'bg-yellow-100 text-yellow-800';
      case 'lead': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Copied!",
        description: `${type} ID copied to clipboard`,
      });
    } catch (error) {
      console.error('Failed to copy:', error);
      toast({
        title: "Copy failed",
        description: "Failed to copy to clipboard",
        variant: "destructive"
      });
    }
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const handleExport = () => {
    if (!data || !data.brands.length) return;

    const headers = [
      'Advertiser',
      'Agency',
      'Email',
      'Vertical',
      'Competitive Categories',
      'Budget (USD)',
      'Target eCPM (USD)',
      'Campaign Window',
      'Preferred Verticals',
      'Status'
    ];

    const rows = data.brands.map((brand) => [
      brand.advertiser,
      brand.agency || '',
      brand.email || '',
      brand.vertical,
      brand.competitive_categories.join('; '),
      brand.budget.toString(),
      brand.target_ecpm.toString(),
      brand.campaign_window,
      brand.preferred_verticals.join('; '),
      brand.status
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row =>
        row
          .map(field => {
            if (field.includes(',') || field.includes('"') || field.includes('\n')) {
              return `"${field.replace(/"/g, '""')}"`;
            }
            return field;
          })
          .join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'demand-view-brands.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No demand data available</p>
      </div>
    );
  }

  const listTotalRaw = Number(data.total);
  const listTotal =
    Number.isFinite(listTotalRaw) && listTotalRaw >= 0 ? Math.floor(listTotalRaw) : 0;
  const demandFiltersActive =
    (filters.status && filters.status !== 'all') ||
    (filters.vertical && filters.vertical !== 'all') ||
    Boolean(filters.budget_min) ||
    Boolean(filters.budget_max) ||
    Boolean(filters.search.trim());

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Demand View</h1>
          <p className="text-gray-600">Brand pipeline and requirements overview</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleExport} disabled={!data?.brands.length}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button
            variant={showFilters ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => setShowFilters((prev) => !prev)}
          >
            <Filter className="h-4 w-4 mr-2" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </Button>
          <Dialog
            open={isCreateDialogOpen}
            onOpenChange={(open) => {
              setIsCreateDialogOpen(open);
              if (open) {
                resetForm();
              }
            }}
          >
            <DialogTrigger asChild>
              <Button size="sm" className="gap-1.5">
                <Plus className="h-4 w-4" />
                <span>Add Brand</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create New Brand</DialogTitle>
              </DialogHeader>
              <BrandForm
                formData={formData}
                setFormData={setFormData}
                formErrors={formErrors}
                onSubmit={handleCreateBrand}
                onCancel={() => {
                  setIsCreateDialogOpen(false);
                  resetForm();
                }}
                verticals={verticals}
                competitiveCategories={competitiveCategories}
                suitabilityOptions={suitabilityOptions}
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Demand Summary Panel */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Brands</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.summary.total_brands}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Committed + Signed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.summary.committed_and_signed}</div>
            <p className="text-xs text-gray-500">
              {((data.summary.committed_and_signed / data.summary.total_brands) * 100).toFixed(1)}% of pipeline
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Pipeline Budget</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(data.summary.total_pipeline_budget)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Committed Budget</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(data.summary.committed_budget)}</div>
            <p className="text-xs text-gray-500">
              {((data.summary.committed_budget / data.summary.total_pipeline_budget) * 100).toFixed(1)}% of total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Avg Target eCPM</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(data.summary.avg_target_ecpm)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Search</label>
                <Input
                  placeholder="Brand name..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Status</label>
                <Select value={filters.status} onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="signed">Signed</SelectItem>
                    <SelectItem value="committed">Committed</SelectItem>
                    <SelectItem value="in_conversation">In Conversation</SelectItem>
                    <SelectItem value="lead">Lead</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Vertical</label>
                <Select value={filters.vertical} onValueChange={(value) => setFilters(prev => ({ ...prev, vertical: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="All verticals" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All verticals</SelectItem>
                    {verticals.map(vertical => (
                      <SelectItem key={vertical} value={vertical}>{vertical}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Min Budget</label>
                <Input
                  type="number"
                  placeholder="0"
                  value={filters.budget_min}
                  onChange={(e) => setFilters(prev => ({ ...prev, budget_min: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Max Budget</label>
                <Input
                  type="number"
                  placeholder="1000000"
                  value={filters.budget_max}
                  onChange={(e) => setFilters(prev => ({ ...prev, budget_max: e.target.value }))}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Brand Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Brands</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Advertiser</TableHead>
                  <TableHead>Agency</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Vertical</TableHead>
                  <TableHead>Competitive Categories</TableHead>
                  <TableHead>Budget</TableHead>
                  <TableHead>Target eCPM</TableHead>
                  <TableHead>Campaign Window</TableHead>
                  <TableHead>Preferred Verticals</TableHead>
                  <TableHead>Preference Signals</TableHead>
                  <TableHead>Script Context Requests</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.brands.map((brand) => (
                  <TableRow key={brand.id} className="hover:bg-gray-50">
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <span>{brand.advertiser}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(brand.id, 'Brand')}
                          className="h-6 w-6 p-0 hover:bg-gray-100"
                          title="Copy Brand ID"
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>{brand.agency || '-'}</TableCell>
                    <TableCell>{brand.email || '-'}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{brand.vertical}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {brand.competitive_categories.map((category, index) => (
                          <Badge key={index} variant="secondary" className="text-xs">
                            {category}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>{formatCurrency(brand.budget)}</TableCell>
                    <TableCell>{formatCurrency(brand.target_ecpm)}</TableCell>
                    <TableCell>{brand.campaign_window}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {brand.preferred_verticals.map((vertical, index) => (
                          <Badge key={index} variant="outline" className="text-xs">
                            {vertical}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      {brand.preference_signals && brand.preference_signals.length > 0 ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedBrandSignals({
                            brand: brand,
                            signals: brand.preference_signals || []
                          })}
                          className="text-xs"
                        >
                          View Signals ({brand.preference_signals.length})
                        </Button>
                      ) : (
                        <span className="text-xs text-gray-400 italic">No signals</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {brand.script_context_requests && brand.script_context_requests.length > 0 ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedBrandScriptRequests({
                            brand: brand,
                            requests: brand.script_context_requests || []
                          })}
                          className="text-xs"
                        >
                          View Requests ({brand.script_context_requests.length})
                        </Button>
                      ) : (
                        <span className="text-xs text-gray-400 italic">No requests</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(brand.status)}>
                        {brand.status.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(brand)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteDialog(brand)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-gray-500 flex items-center gap-2">
              <span>
                {listTotal === 0 ? (
                demandFiltersActive ? 'No brands match your filters.' : 'No brands found.'
              ) : (
                `Showing ${((page - 1) * PAGE_SIZE) + 1} to ${Math.min(page * PAGE_SIZE, listTotal)} of ${listTotal} brands`
              )}
              </span>
              {isFetching && (
                <span className="inline-flex items-center gap-1 text-blue-600">
                  <span className="h-3 w-3 rounded-full border-2 border-blue-600 border-b-transparent animate-spin" />
                  Updating...
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1 || isFetching}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={page * PAGE_SIZE >= listTotal || isFetching}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preference Signals Dialog */}
      <Dialog open={!!selectedBrandSignals} onOpenChange={() => setSelectedBrandSignals(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Preference Signals - {selectedBrandSignals?.brand.advertiser}
            </DialogTitle>
          </DialogHeader>

          {selectedBrandSignals && (
            <div className="space-y-4">
              <div className="text-sm text-gray-600">
                Total signals: {selectedBrandSignals.signals.length}
              </div>

              <div className="space-y-3">
                {selectedBrandSignals.signals.map((signal, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 border rounded-lg">
                    <div className="flex-shrink-0 mt-1">
                      {signal.direction === 'up' ? (
                        <TrendingUp className="h-4 w-4 text-green-600" />
                      ) : (
                        <TrendingDown className="h-4 w-4 text-red-600" />
                      )}
                    </div>

                    <div className="flex-1">
                      <div className="font-medium text-gray-900">
                        {signal.show_name || 'Unknown Project'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {signal.direction === 'up' ? 'Positive signal' : 'Negative signal'}
                      </div>
                      {signal.created_at && (
                        <div className="text-xs text-gray-400 mt-1">
                          {new Date(signal.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {selectedBrandSignals.signals.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No preference signals found for this brand.
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Script Context Requests Dialog */}
      <Dialog open={!!selectedBrandScriptRequests} onOpenChange={() => setSelectedBrandScriptRequests(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Script Context Requests - {selectedBrandScriptRequests?.brand.advertiser}
            </DialogTitle>
          </DialogHeader>

          {selectedBrandScriptRequests && (
            <div className="space-y-4">
              <div className="text-sm text-gray-600">
                Total requests: {selectedBrandScriptRequests.requests.length}
              </div>

              <div className="space-y-3">
                {selectedBrandScriptRequests.requests.map((request, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 border rounded-lg">
                    <div className="flex-shrink-0 mt-1">
                      <FileText className="h-4 w-4 text-blue-600" />
                    </div>

                    <div className="flex-1">
                      <div className="font-medium text-gray-900">
                        {request.project_name || 'Unknown Project'}
                      </div>
                      <div className="text-sm text-gray-500">
                        Campaign: {request.campaign_name || 'Unknown Campaign'}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        {request.message || 'No message provided'}
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            request.status === 'approved' ? 'bg-green-100 text-green-800' :
                              request.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                          }`}>
                          {request.status || 'pending'}
                        </span>
                        {request.created_at && (
                          <div className="text-xs text-gray-400">
                            {new Date(request.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {selectedBrandScriptRequests.requests.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No script context requests found for this brand.
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Brand Dialog */}
      <Dialog
        open={isEditDialogOpen}
        onOpenChange={(open) => {
          setIsEditDialogOpen(open);
          if (!open) {
            setEditingBrand(null);
            resetForm();
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Brand</DialogTitle>
          </DialogHeader>
          <BrandForm
            formData={formData}
            setFormData={setFormData}
            formErrors={formErrors}
            onSubmit={handleUpdateBrand}
            onCancel={() => {
              setIsEditDialogOpen(false);
              setEditingBrand(null);
              resetForm();
            }}
            verticals={verticals}
            competitiveCategories={competitiveCategories}
            suitabilityOptions={suitabilityOptions}
            isEditing={true}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Brand Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Brand</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Are you sure you want to delete <strong>{brandToDelete?.advertiser}</strong>?
              This action cannot be undone and will also remove:
            </p>
            <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
              <li>All associated campaigns</li>
              <li>Preference signals</li>
              <li>Script context requests</li>
              <li>Slot assignments</li>
              <li>Related packages</li>
            </ul>
            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setIsDeleteDialogOpen(false);
                  setBrandToDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteBrand}
              >
                Delete Brand
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Brand Form Component
interface BrandFormProps {
  formData: BrandFormData;
  setFormData: React.Dispatch<React.SetStateAction<BrandFormData>>;
  formErrors: BrandFormErrors;
  onSubmit: () => void;
  onCancel: () => void;
  verticals: string[];
  competitiveCategories: string[];
  suitabilityOptions: string[];
  isEditing?: boolean;
}

const BrandForm: React.FC<BrandFormProps> = ({
  formData,
  setFormData,
  formErrors,
  onSubmit,
  onCancel,
  verticals,
  competitiveCategories,
  suitabilityOptions,
  isEditing = false
}) => {
  const handleArrayFieldChange = (field: keyof BrandFormData, value: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked
        ? [...(prev[field] as string[]), value]
        : (prev[field] as string[]).filter(item => item !== value)
    }));
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="advertiser">Advertiser *</Label>
          <Input
            id="advertiser"
            value={formData.advertiser}
            onChange={(e) => setFormData(prev => ({ ...prev, advertiser: e.target.value }))}
            required
          />
          {formErrors.advertiser && <p className="text-xs text-red-600 mt-1">{formErrors.advertiser}</p>}
        </div>
        <div>
          <Label htmlFor="agency">Agency</Label>
          <Input
            id="agency"
            value={formData.agency}
            onChange={(e) => setFormData(prev => ({ ...prev, agency: e.target.value }))}
          />
        </div>
      </div>

      <div>
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
          placeholder="contact@brand.com"
          disabled={isEditing}
        />
        {formErrors.email && <p className="text-xs text-red-600 mt-1">{formErrors.email}</p>}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="vertical">Vertical *</Label>
          <Select value={formData.vertical} onValueChange={(value) => setFormData(prev => ({ ...prev, vertical: value }))}>
            <SelectTrigger>
              <SelectValue placeholder="Select vertical" />
            </SelectTrigger>
            <SelectContent>
              {verticals.map(vertical => (
                <SelectItem key={vertical} value={vertical}>{vertical}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {formErrors.vertical && <p className="text-xs text-red-600 mt-1">{formErrors.vertical}</p>}
        </div>
        <div>
          <Label htmlFor="status">Status *</Label>
          <Select value={formData.status} onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}>
            <SelectTrigger>
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="lead">Lead</SelectItem>
              <SelectItem value="in_conversation">In Conversation</SelectItem>
              <SelectItem value="committed">Committed</SelectItem>
              <SelectItem value="signed">Signed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="budget">Budget (USD) *</Label>
          <Input
            id="budget"
            type="number"
            value={formData.budget}
            onChange={(e) => setFormData(prev => ({ ...prev, budget: Number(e.target.value) }))}
            required
          />
          {formErrors.budget && <p className="text-xs text-red-600 mt-1">{formErrors.budget}</p>}
        </div>
        <div>
          <Label htmlFor="target_ecpm">Target eCPM (USD) *</Label>
          <Input
            id="target_ecpm"
            type="number"
            step="0.01"
            value={formData.target_ecpm}
            onChange={(e) => setFormData(prev => ({ ...prev, target_ecpm: Number(e.target.value) }))}
            required
          />
          {formErrors.target_ecpm && <p className="text-xs text-red-600 mt-1">{formErrors.target_ecpm}</p>}
        </div>
      </div>

      <div>
        <Label htmlFor="campaign_window">Campaign Window *</Label>
        <Input
          id="campaign_window"
          value={formData.campaign_window}
          onChange={(e) => setFormData(prev => ({ ...prev, campaign_window: e.target.value }))}
          placeholder="e.g., Q3 2026"
          required
        />
        {formErrors.campaign_window && <p className="text-xs text-red-600 mt-1">{formErrors.campaign_window}</p>}
      </div>

      <div>
        <Label>Competitive Categories</Label>
        <div className="grid grid-cols-2 gap-2 mt-2 max-h-32 overflow-y-auto">
          {competitiveCategories.map(category => (
            <label key={category} className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.competitive_categories.includes(category)}
                onChange={(e) => handleArrayFieldChange('competitive_categories', category, e.target.checked)}
              />
              <span className="text-sm">{category}</span>
            </label>
          ))}
        </div>
        {formErrors.competitive_categories && <p className="text-xs text-red-600 mt-1">{formErrors.competitive_categories}</p>}
      </div>

      <div>
        <Label>Preferred Verticals</Label>
        <div className="grid grid-cols-2 gap-2 mt-2 max-h-32 overflow-y-auto">
          {verticals.map(vertical => (
            <label key={vertical} className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.preferred_verticals.includes(vertical)}
                onChange={(e) => handleArrayFieldChange('preferred_verticals', vertical, e.target.checked)}
              />
              <span className="text-sm">{vertical}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <Label>Suitability Requirements</Label>
        <div className="grid grid-cols-2 gap-2 mt-2 max-h-32 overflow-y-auto">
          {suitabilityOptions.map(option => (
            <label key={option} className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.suitability_requirements.includes(option)}
                onChange={(e) => handleArrayFieldChange('suitability_requirements', option, e.target.checked)}
              />
              <span className="text-sm">{option}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          value={formData.notes}
          onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
          rows={3}
        />
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={onSubmit}>
          {isEditing ? 'Update' : 'Create'} Brand
        </Button>
      </div>
    </div>
  );
};

export default DemandViewPage;